import { useTranslation } from "react-i18next";
import { Paperclip, Download, X } from "lucide-react";
import { Modal } from "../ui/Modal/Modal";
import { LoadingSpinner } from "../ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../ui/ErrorFallback/ErrorFallback";
import {
	useOrderAttachments,
	orderAttachmentDownloadUrl,
	allOrderAttachmentsDownloadUrl,
} from "../../hooks/use-orders";
import styles from "./OrderAttachmentsModal.module.css";

interface OrderAttachmentsModalProps {
	orderId: string | null;
	documentNumber?: string | null | undefined;
	onClose: () => void;
}

/** Sadece görüntüleme/indirme — dosya eklemek için siparişi Düzenle'den açmak gerekir. */
export function OrderAttachmentsModal({ orderId, documentNumber, onClose }: OrderAttachmentsModalProps) {
	const { t } = useTranslation();
	const query = useOrderAttachments(orderId);
	const items = query.data?.items ?? [];

	return (
		<Modal isOpen={!!orderId} onClose={onClose} maxWidth={480}>
			<div className={styles.root}>
				<div className={styles.topBar}>
					<Paperclip size={18} />
					<span className={styles.topBarTitle}>
						{t("attachments")}
						{documentNumber ? ` — ${documentNumber}` : ""}
					</span>
					<button type="button" className={styles.topBarClose} onClick={onClose} aria-label={t("close")}>
						<X size={18} />
					</button>
				</div>

				<div className={styles.body}>
					{query.isLoading && <LoadingSpinner />}
					{query.isError && <ErrorFallback onRetry={query.refetch} />}
					{!query.isLoading && !query.isError && (
						<div className={styles.list}>
							{items.length === 0 ? (
								<div className={styles.empty}>{t("noAttachments")}</div>
							) : (
								<>
									{items.length > 1 && (
										<a
											className={styles.downloadAll}
											href={allOrderAttachmentsDownloadUrl(orderId as string)}
										>
											<Download size={14} />
											{t("downloadAll")}
										</a>
									)}
									{items.map((item) => (
										<div
											key={`${item.source}-${item.source === "sap" ? item.line : item.index}`}
											className={styles.item}
										>
											<a
												className={styles.itemName}
												href={orderAttachmentDownloadUrl(orderId as string, item, { inline: true })}
												target="_blank"
												rel="noreferrer"
											>
												{item.name}
											</a>
											<a
												className={styles.itemDownload}
												href={orderAttachmentDownloadUrl(orderId as string, item)}
												aria-label={t("download")}
											>
												<Download size={16} />
											</a>
										</div>
									))}
								</>
							)}
						</div>
					)}
				</div>
			</div>
		</Modal>
	);
}
